import { useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface SchemaField {
  key: string;
  title: string;
  type: string;
  enumValues?: string; // comma-separated for select type
}

interface JsonSchemaProperty {
  type: string;
  title: string;
  format?: string;
  enum?: string[];
}

interface JsonSchema {
  type: 'object';
  properties: Record<string, JsonSchemaProperty>;
}

const FIELD_TYPES = [
  { value: 'string', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'integer', label: 'Integer' },
  { value: 'boolean', label: 'Yes/No' },
  { value: 'select', label: 'Select (Dropdown)' },
  { value: 'date', label: 'Date' },
  { value: 'textarea', label: 'Long Text' },
];

function schemaToFields(schema: Record<string, unknown>): SchemaField[] {
  const props = (schema as unknown as JsonSchema)?.properties;
  if (!props || typeof props !== 'object') return [];
  return Object.entries(props).map(([key, prop]) => {
    let type = prop.type || 'string';
    let enumValues: string | undefined;

    if (type === 'string' && prop.enum) {
      type = 'select';
      enumValues = prop.enum.join(', ');
    } else if (type === 'string' && prop.format === 'textarea') {
      type = 'textarea';
    } else if (type === 'string' && prop.format === 'date') {
      type = 'date';
    }

    return { key, title: prop.title || key, type, enumValues };
  });
}

function fieldsToSchema(fields: SchemaField[]): Record<string, unknown> {
  if (fields.length === 0) return {};
  const properties: Record<string, JsonSchemaProperty> = {};
  for (const field of fields) {
    const key = field.key || field.title.toLowerCase().replace(/\s+/g, '_');

    switch (field.type) {
      case 'select': {
        const enumArr = (field.enumValues ?? '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        properties[key] = { type: 'string', title: field.title, enum: enumArr.length > 0 ? enumArr : undefined };
        break;
      }
      case 'textarea':
        properties[key] = { type: 'string', title: field.title, format: 'textarea' };
        break;
      case 'date':
        properties[key] = { type: 'string', title: field.title, format: 'date' };
        break;
      case 'integer':
        properties[key] = { type: 'integer', title: field.title };
        break;
      default:
        properties[key] = { type: field.type, title: field.title };
        break;
    }
  }
  return { type: 'object', properties };
}

interface SchemaFieldBuilderProps {
  value: Record<string, unknown>;
  onChange: (schema: Record<string, unknown>) => void;
}

export function SchemaFieldBuilder({ value, onChange }: SchemaFieldBuilderProps) {
  const fields = schemaToFields(value);

  const update = useCallback(
    (updatedFields: SchemaField[]) => {
      onChange(fieldsToSchema(updatedFields));
    },
    [onChange],
  );

  const addField = () => {
    update([...fields, { key: '', title: '', type: 'string' }]);
  };

  const removeField = (index: number) => {
    update(fields.filter((_, i) => i !== index));
  };

  const updateField = (index: number, patch: Partial<SchemaField>) => {
    const updated = fields.map((f, i) => {
      if (i !== index) return f;
      const merged = { ...f, ...patch };
      if (patch.title && !f.key) {
        merged.key = patch.title.toLowerCase().replace(/\s+/g, '_');
      }
      return merged;
    });
    update(updated);
  };

  return (
    <div className="flex flex-col gap-3">
      {fields.length > 0 && (
        <div className="flex flex-col gap-2">
          {fields.map((field, index) => (
            <div key={index} className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Input
                  value={field.title}
                  className="flex-1"
                  placeholder="Field name"
                  onChange={(e) => updateField(index, { title: e.target.value })}
                />
                <Select
                  value={field.type}
                  onValueChange={(type) => updateField(index, { type })}
                >
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map((ft) => (
                      <SelectItem key={ft.value} value={ft.value}>
                        {ft.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="icon"
                  type="button"
                  variant="ghost"
                  className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive"
                  aria-label={`Remove field ${field.title || index + 1}`}
                  onClick={() => removeField(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              {field.type === 'select' && (
                <Input
                  value={field.enumValues ?? ''}
                  className="ml-0 text-sm"
                  placeholder="Options (comma-separated, e.g. Option A, Option B)"
                  onChange={(e) => updateField(index, { enumValues: e.target.value })}
                />
              )}
            </div>
          ))}
        </div>
      )}
      <Button
        size="sm"
        type="button"
        variant="outline"
        className="w-fit"
        onClick={addField}
      >
        <Plus className="mr-2 h-4 w-4" />
        Add Field
      </Button>
    </div>
  );
}
